import { useMemo } from 'react';
import { Category } from '../types';
import { hasSelectedParent, isDirectlySelected } from '../utils';

// MEMO: categoriesByLevels 제거시 전체 삭제 예정
export const useCategorySelector = (
  categoriesByLevels: Array<Array<Category>>,
  selectedKeys: Array<string>,
  multiple?: boolean,
) => {
  // 카테고리 키를 카테고리 객체로 매핑한 맵
  const categoryMap = useMemo(() => {
    const map = new Map<string, Category>();
    categoriesByLevels.flat().forEach((category) => {
      map.set(category.key, category);
    });
    return map;
  }, [categoriesByLevels]);

  /**
   * 선택된 키의 경로를 찾는 함수
   * @returns 각 레벨의 부모 키 배열
   */
  const findCategoryPath = useMemo(() => {
    const targetKey = selectedKeys[0];
    const path: string[] = new Array(categoriesByLevels.length).fill('');
    const targetCategory = categoryMap.get(targetKey);
    if (!targetCategory) {
      return path;
    }
    // targetCategory의 부모부터 시작해서 루트까지 올라가며 경로 구성(targetKey 자체는 포함하지 않고, 부모들만 path에 저장 - selectedCategoryKeys 구조와 싱크)
    let currentCategory: Category | undefined = categoryMap.get(targetCategory.parentKey);
    while (currentCategory) {
      // path[level + 1]에 저장
      path[currentCategory.level + 1] = currentCategory.key;
      currentCategory = categoryMap.get(currentCategory.parentKey);
    }
    return path;
  }, [categoriesByLevels, selectedKeys, categoryMap]);

  /**
   * 모든 카테고리의 checked/indeterminate 상태 캐시
   * selectedKeys가 변경될 때만 다시 계산함
   */
  const categoryStates = useMemo(() => {
    if (!multiple) {
      return null;
    }

    const checkedMap = new Map<string, boolean>();
    const indeterminateMap = new Map<string, boolean>();

    const calculateStates = (category: Category): { checked: boolean; indeterminate: boolean } => {
      const cachedChecked = checkedMap.get(category.key);
      const cachedIndeterminate = indeterminateMap.get(category.key);

      if (cachedChecked !== undefined && cachedIndeterminate !== undefined) {
        return { checked: cachedChecked, indeterminate: cachedIndeterminate };
      }

      // 직접 선택되었는지 확인(단일 선택일 경우 바로 리턴)
      if (isDirectlySelected(category, selectedKeys)) {
        checkedMap.set(category.key, true);
        indeterminateMap.set(category.key, false);
        return { checked: true, indeterminate: false };
      }

      // 부모가 선택되어 자식이 체크된 상태인지 확인
      if (category.parentKey) {
        if (hasSelectedParent(category, categoryMap, selectedKeys)) {
          checkedMap.set(category.key, true);
          indeterminateMap.set(category.key, false);
          return { checked: true, indeterminate: false };
        }
      }

      // 자식이 없는 경우 (리프 노드)
      if (!category.childrenKeys || category.childrenKeys.length === 0) {
        checkedMap.set(category.key, false);
        indeterminateMap.set(category.key, false);
        return { checked: false, indeterminate: false };
      }

      // 자식들의 상태를 확인
      const enabledChildren = category.childrenKeys
        .map((key) => categoryMap.get(key))
        .filter((child) => child && !child.disabled) as Category[];

      if (enabledChildren.length === 0) {
        checkedMap.set(category.key, false);
        indeterminateMap.set(category.key, false);
        return { checked: false, indeterminate: false };
      }

      // 자식들의 상태 계산
      const childStates = enabledChildren.map((child) => calculateStates(child));
      const allChildrenChecked = childStates.every((state) => state.checked);
      const someChildrenCheckedOrIndeterminate = childStates.some((state) => state.checked || state.indeterminate);

      let checked = false;
      let indeterminate = false;

      if (allChildrenChecked) {
        checked = true;
        indeterminate = false;
      } else if (someChildrenCheckedOrIndeterminate) {
        checked = false;
        indeterminate = true;
      }

      checkedMap.set(category.key, checked);
      indeterminateMap.set(category.key, indeterminate);
      return { checked, indeterminate };
    };

    // 모든 카테고리에 대해 상태 계산
    categoriesByLevels.flat().forEach((category) => {
      calculateStates(category);
    });

    return { checkedMap, indeterminateMap };
  }, [categoriesByLevels, selectedKeys, categoryMap, multiple]);

  return {
    categoryMap,
    categoryStates,
    findCategoryPath,
  };
};
